/** Sending the attorney's queued figure-definition changes to the store, one at a time. */

import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import type { FigureListResponse } from "@patent-studio/api-types";
import { type Definitions, definitionsOf } from "@/lib/element-registry";
import { ApiError } from "@/services/api-client";
import { listFigures, replaceFigureDefinitions } from "@/services/figures";
import { termsKey } from "./query-keys";
import type { DefinitionChange } from "./use-figure-definitions";

/** What a write needs from the definitions hook: its cache, its fold and its pending list. */
export interface DefinitionsWrite {
  qc: QueryClient;
  key: QueryKey;
  fold: (change: DefinitionChange, definitions: Definitions) => Definitions;
  pending: { current: readonly DefinitionChange[] };
  setPending: (changes: readonly DefinitionChange[]) => void;
}

/**
 * Put a set the store has acknowledged into the cache, leaving the rest of the screen alone.
 *
 * In-flight reads are cancelled first: a refetch that started before this set was applied
 * would land after it carrying an older one.
 */
async function adopt(
  qc: QueryClient,
  key: QueryKey,
  applied: Definitions,
): Promise<void> {
  await qc.cancelQueries({ queryKey: key });
  const showing = qc.getQueryData<FigureListResponse>(key);
  if (showing === undefined) return;
  qc.setQueryData(key, { ...showing, definitions: applied });
}

/**
 * Send one change, built against the freshest acknowledged set, reconciling once if the
 * store has moved on.
 *
 * Outside the hook because it needs none of its state. The cache holds only what the store
 * has acknowledged, so the body is that set with this one change folded in; the changes
 * still queued behind this one ride in their own writes. What comes back is the set the
 * store now holds, and adopting it is what keeps the next write's base honest.
 */
async function writeChange(
  { qc, key, fold }: DefinitionsWrite,
  task: string,
  change: DefinitionChange,
): Promise<void> {
  const send = () => {
    const server = definitionsOf(
      qc.getQueryData<FigureListResponse>(key)?.definitions,
    );
    const built = fold(change, server);
    // The fold settled as identity, so the store already holds everything this change
    // asks for and there is nothing to send. This is the queued-collision case: a bend
    // whose curve was straightened first, a head whose line was taken away, a change for
    // a mark that has since been removed. The rules hand back the very set they were
    // given when the mark a change names has moved on, which the instance seam guarantees
    // structurally rather than leaving each rule to remember.
    if (built === server) return null;
    return replaceFigureDefinitions(task, built);
  };
  try {
    const applied = await send();
    if (applied !== null) await adopt(qc, key, definitionsOf(applied));
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
    // The stored set is no longer the one this change was built against, which a figure
    // deleted mid-edit is enough to cause. The change is still what the attorney asked
    // for, so the store's set is adopted and the change is folded over it and sent once
    // more. Once only: two screens editing one task refuse each other, and a retry per
    // refusal is a loop rather than a reconciliation.
    const fresh = await listFigures(task);
    await adopt(qc, key, definitionsOf(fresh.definitions));
    const applied = await send();
    if (applied !== null) await adopt(qc, key, definitionsOf(applied));
  }
}

/**
 * One queued change's turn: written if it is still wanted, then taken off the pending list
 * whether it landed or failed. A failure is told in a toast and handed on to whoever waits.
 */
export async function writeQueued(
  queue: DefinitionsWrite,
  taskId: string,
  change: DefinitionChange,
  save: (change: DefinitionChange) => Promise<void>,
): Promise<void> {
  const { qc, key, pending, setPending } = queue;
  // Still wanted, or dropped while it waited. A change to a figure being deleted is
  // taken off the list when the delete is asked for, and sending it anyway would be
  // refused for naming a file that is going, turning a deliberate delete into a save
  // error about work the attorney chose to throw away.
  if (!pending.current.includes(change)) return;
  try {
    await writeChange(queue, taskId, change);
  } catch (error) {
    toast.error("Could not save that change.", {
      action: {
        label: "Retry",
        onClick: () => {
          save(change).catch(() => undefined);
        },
      },
    });
    throw error;
  } finally {
    // Taken off whether the write landed or failed: an acknowledged change is in the
    // adopted set, and a refused one is not what the store holds. Between the adopt
    // above and this removal nothing renders, so no frame shows the change doubled
    // or missing.
    pending.current = pending.current.filter((held) => held !== change);
    setPending(pending.current);
    // Only a drained queue reconciles, as a safety net for whatever a definitions
    // write does not return. Refetching between two queued writes would pull in a
    // set the next write is about to replace, and show it in the gap.
    // The terms are read again with it: a component's title is its term, and this
    // write goes past the mutation cache the terms query listens to.
    if (pending.current.length === 0) {
      await Promise.all([
        qc.invalidateQueries({ queryKey: key }),
        qc.invalidateQueries({ queryKey: termsKey(taskId) }),
      ]);
    }
  }
}
